import TrackingEvent from "../TrackingEvent.interface";
import BaseProvider from "./BaseProvider";
import {getPageType} from "libs/Tracking/helpers";

type frEvent = {
  name: string,
  params: {
    page_type?: string,
    coupon_id?: string,
    coupon_title?: string,
    shop_id?: string,
    shop_name?: string,
    shop_id_landing?: string,
    coupon_position?: string,
    coupon_element?: string,
    coupon_area?: string,
    goal_type?: string,
    anchor?: string,
    link_type?: string
  }
}

class FRTracking extends BaseProvider {
  private pageType = "null";
  private privacyConfigured = false;

  constructor() {
    super();

    if (this.isLoaded()) {
      this.pageType = getPageType();
      const shopId = window.eventLayer[0].shop_id || "null";
      this.onPageShow(shopId);
      this.setPrivacyFromCMP();
    }
  }

  protected prepareEvent(eventData: TrackingEvent): frEvent | { name: null } {
    const readyEvent: frEvent = {
      name: eventData.type,
      params: {
        page_type: this.pageType,
        coupon_id: eventData.coupon_id,
        coupon_title: eventData.coupon_title,
        shop_id: eventData.shop_id,
        shop_name: eventData.shop_name,
        shop_id_landing: eventData.shop_id_landing,
        coupon_position: eventData.coupon_position,
        coupon_element: eventData.coupon_element,
        ...(isLinkDataRequired()) && {
          coupon_area: eventData.coupon_area,
          anchor: eventData.anchor,
          link_type: eventData.link_type
        },
        ...(eventData.type === 'clickout') && {goal_type: 'coupon_clickout'}
      }
    }

    if (isValidEventType()) {
      return readyEvent;
    } else {
      return {name: null}
    }

    function isValidEventType() {
      return [
        'clickout',
        'non-clickout',
        'click',
        'newsletter',
        'earthMark',
        'donation'
      ].includes(eventData.type);
    }

    function isLinkDataRequired() {
      return eventData.type === 'click' || eventData.type === 'earthMark' || eventData.type === 'donation';
    }
  }

  protected onPageShow(shopId: string) {

    window.siteTracker.page({
      page_type: this.pageType,
      shop_id: shopId
    });
  }

  protected isLoaded(): boolean {
    return typeof window.siteTracker !== 'undefined';
  }

  sendProviderEvent(Event: TrackingEvent) {
    const frEvent = this.prepareEvent(Event);

    if (this.isLoaded() && frEvent.name !== null) {
      window.siteTracker.track(frEvent.name, {
        ...frEvent.params
      });
    }
  }

  setPrivacyFromCMP() {
    document.addEventListener('gdprConsentGiven', this.handleConsentGiven);
    document.addEventListener('gdprCmpLoadFailed', this.handleCmpLoadFailed, {once: true});
    this.subscribeToCMP();
  }

  private handleConsentGiven = () => {
    this.subscribeToCMP();
  };

  private handleCmpLoadFailed = () => {
    this.applyPrivacy('denied');
  };

  private subscribeToCMP() {
    if (this.privacyConfigured || typeof window.__tcfapi !== 'function') {
      return;
    }

    window.__tcfapi('addEventListener', 2, (tcData, success) => {
      const consentResolved = success
        && (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete');

      if (!consentResolved) {
        return;
      }

      if (tcData.gdprApplies === false) {
        this.applyPrivacy('granted');
        return;
      }

      const purposeConsents: Record<number, boolean> = tcData.purpose?.consents ?? {};
      const analyticsGranted = !!purposeConsents[1] && (!!purposeConsents[8] || !!purposeConsents[9]);

      this.applyPrivacy(analyticsGranted ? 'granted' : 'denied');
    });
  }

  private applyPrivacy(mode: 'granted' | 'denied') {
    if (this.privacyConfigured || !this.isLoaded()) {
      return;
    }

    this.privacyConfigured = true;
    document.removeEventListener('gdprConsentGiven', this.handleConsentGiven);
    window.siteTracker.consent(mode);
  }
}

const frTracker = new FRTracking();

export default frTracker;
